import { AbilityBuilder, createMongoAbility, type MongoAbility } from "@casl/ability";
import { useAbility as useCaslAbility } from "@casl/vue";
import type { TPermission } from "@/api/models/practice/practiceStaff";

import {
  ESubjectsGenerated as ESubjects,
  EActionsGenerated as EActions,
  CUSTOM_ACTIONS_BY_SUBJECT,
} from "./ability.generated";

export { ESubjects, EActions };

/**
 * Backwards-compatible alias. `EActions` already carries every CRUD and custom
 * action, so `PermActions.READ`, `$permActions.MANAGE_BILLING`, … all resolve.
 */
export const PermActions = EActions;

/** App-specific actions that are not part of the API permission list. */
export enum ECustomActions {
  IMPERSONATE = "impersonate",
}

/** The four CRUD actions every subject supports implicitly. */
export type TCrudAction = EActions.CREATE | EActions.READ | EActions.UPDATE | EActions.DELETE;

/** Any action string accepted by `can()` — the string-literal form of every `EActions` value. */
export type TPermissionAction = `${EActions}`;

/**
 * Actions valid for a given subject: CRUD always, plus that subject's custom
 * actions (if any). This is the subject-centric counterpart the generated
 * `CUSTOM_ACTIONS_BY_SUBJECT` map powers.
 */
export type TActionsForSubject<S extends ESubjects> =
  | TCrudAction
  | (S extends keyof typeof CUSTOM_ACTIONS_BY_SUBJECT
      ? (typeof CUSTOM_ACTIONS_BY_SUBJECT)[S][number]
      : never);

type TActions = EActions | string;
type TSubjects = ESubjects | string;
export type TEntityPermissions = string[];
export type TEntityPermissionMode = "any" | "all";

export type TAppAbility = Omit<MongoAbility<[TActions, TSubjects]>, "can"> & {
  can<S extends ESubjects>(
    action: TActionsForSubject<S>,
    subject: S,
    entityPermissions?: TEntityPermissions,
    mode?: TEntityPermissionMode
  ): boolean;
};

/** Standard Django CRUD verb -> canonical CASL action. */
const CRUD_VERB_TO_ACTION = {
  add: EActions.CREATE,
  change: EActions.UPDATE,
  delete: EActions.DELETE,
  view: EActions.READ,
} as const;

const ACTION_TO_CRUD_VERB = Object.fromEntries(
  Object.entries(CRUD_VERB_TO_ACTION).map(([verb, action]) => [action, verb])
) as Partial<Record<EActions, string>>;

function normalizeSubject(subject: string): string {
  return subject.replace(/__/g, ".");
}

function hasEntityPermission(
  entityPermissions: TEntityPermissions,
  action: string,
  subject: string
): boolean {
  const normalizedSubject = normalizeSubject(subject);
  const appLabel = normalizedSubject.slice(0, normalizedSubject.indexOf("."));
  const model = normalizedSubject.slice(normalizedSubject.indexOf(".") + 1);

  const verb = ACTION_TO_CRUD_VERB[action as EActions];
  if (verb) {
    return entityPermissions.includes(`${appLabel}.${verb}_${model}`);
  }

  return entityPermissions.includes(`${appLabel}.${action}`);
}

/**
 * Resolve a raw Django permission codename to its canonical CRUD action for a
 * subject, e.g. `view_practicestaff` on `practice.practicestaff` -> `read`.
 * Returns `null` for custom actions (e.g. `change_practicestaff_me`), which are
 * granted verbatim and never fold into a general CRUD grant.
 */
function crudActionFor(action: string, subject: string): EActions | null {
  const model = subject.slice(subject.indexOf(".") + 1);
  const modelNoOld = model.replace(/old$/, "");
  for (const [verb, crud] of Object.entries(CRUD_VERB_TO_ACTION)) {
    if (action === `${verb}_${model}` || action === `${verb}_${modelNoOld}` || action === verb) {
      return crud;
    }
  }
  return null;
}

function grantPermission(
  grant: (action: string, subject: string) => void,
  action: string,
  subject: string
) {
  const normalizedSubject = normalizeSubject(subject);
  grant(action, normalizedSubject);

  const crud = crudActionFor(action, normalizedSubject);
  if (crud) {
    grant(crud, normalizedSubject);
  }
}

function createAppAbility(): TAppAbility {
  const instance = createMongoAbility<MongoAbility<[TActions, TSubjects]>>();
  const baseCan = instance.can.bind(instance);

  const can: TAppAbility["can"] = (action, subject, entityPermissions, mode = "any") => {
    if (!entityPermissions) {
      return baseCan(action, subject);
    }

    const globalGrant = baseCan(action, subject);
    const entityGrant = hasEntityPermission(entityPermissions, action, subject);

    return mode === "all" ? globalGrant && entityGrant : globalGrant || entityGrant;
  };

  return Object.assign(instance, { can });
}

export const ability = createAppAbility();

export function useAbility(): TAppAbility {
  return useCaslAbility() as TAppAbility;
}

export function buildAbility(perms: TPermission[]) {
  const { can, build } = new AbilityBuilder<MongoAbility<[TActions, TSubjects]>>(
    createMongoAbility
  );
  const seen = new Set<string>();

  const grant = (action: string, subject: string) => {
    const key = `${action}|${subject}`;
    if (seen.has(key)) return;
    seen.add(key);
    can(action, subject);
  };

  for (const p of perms) {
    grantPermission(grant, p.action, p.subject);
  }

  return build();
}
